import { InfoCircle, Locked, WarningSolid } from '@wfp/icons-react';
import {
  Button,
  Modal,
  Module,
  ModuleBody,
  ModuleFooter,
  ModuleHeader,
  Tag,
} from '@wfp/react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Editor as TinyMCEEditor } from 'tinymce';

import ConfirmationModal from 'src/components/ConfirmationModal';
import AnchorModal from 'src/components/ReportTemplate/anchorModal';
import AiActionButton from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/AiActionButton';
import AiQuotaNotice from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/AiQuotaNotice';
import AiStatusAnnouncer from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/AiStatusAnnouncer';
import ContextualActionsHint from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/ContextualActionsHint';
import DraftGenerationSkeleton from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/DraftGenerationSkeleton';
import DraftPreviewPanel from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/DraftPreviewPanel';
import useAiNarrative from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/useAiNarrative';
import TinyEditorWrapper from 'src/components/ReportTemplate/WorkspaceACR2023/TinyEditorWrapper';
import VersionItem from 'src/components/ReportTemplate/WorkspaceACR2023/VersionItem';
import VersionsModal from 'src/components/ReportTemplate/WorkspaceACR2023/VersionsModal';
import {
  AI_MIN_SELECTION_WORDS,
  AI_SUGGESTION_CONTENT_STYLE,
  API_HOST,
  narrativeAutosaveInterval,
  SUCCESS_REQUEST,
  UPDATE,
} from 'src/constants';
import { requestAPI, requestAPIHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import { COMMENTS_LIST, COMMENTS_TARGET_ID } from 'src/redux/constants';
import {
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import {
  cancelMultiNarrative,
  forceUnlockMultiNarrative,
  lockMultiNarrative,
  receiveMultiNarrativeSingleResponse,
  resetMultiNarrative,
  restoreVersionMultiNarrative,
  saveMultiNarrative,
} from 'src/redux/narrative/actions';
import { RootState } from 'src/redux/store';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import type { INarrative, ISelectedVersion } from 'src/types';
import {
  countWords,
  getPlainText,
  sendGAEvent,
  showNotification,
} from 'src/utils';

interface Props {
  narrative: INarrative;
  isEditEnable: boolean;
  isForcePostEnabled: boolean;
}

const NarrativeErrorMessage = ({
  wordsCountResume,
}: {
  wordsCountResume: string;
}) => {
  return (
    <div className="narrative-footer narrative-error-msg">
      <WarningSolid color="#E4002B" />
      <div>Word count limit exceed: {wordsCountResume}</div>
    </div>
  );
};

const Narrative: FC<Props> = ({
  narrative,
  isEditEnable,
  isForcePostEnabled,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<ISelectedVersion | null>(null);

  const [autosave, setAutosave] = useState(false);
  const [tinyEditor, setTinyEditor] = useState<TinyMCEEditor | null>(null);

  const viewState = useSelector((state: RootState) => state.viewState);
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY].data,
  );
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const multinarrativeIsFetching = useSelector(
    (state: RootState) =>
      state.viewData[SECTION_DETAIL_DATA_KEY].isFetchingMultinarrative,
  );

  const dispatch = useDispatch();

  const {
    id,
    is_optional: isOptional,
    limit,
    title,
    i_am_editor: iAmEditor,
    content: initialContent,
    editor,
    locked_at: lockedAt,
    saved = false,
    autosaved = false,
    canceled = false,
    locked = false,
    info,
  } = narrative;

  const [content, setContent] = useState(initialContent || '');

  const getCurrentWordCount = () => {
    return countWords(getPlainText(content));
  };

  const wordsExceed = getCurrentWordCount() > limit;

  const narrativeEditedByOthers = !iAmEditor && editor !== null;
  const imEditing = isEdit || iAmEditor;
  const notPublished = reportDetailData?.state !== 'Published';

  const isFetchingCurrentNarrative = useMemo(
    () => multinarrativeIsFetching && multinarrativeIsFetching === id,
    [multinarrativeIsFetching, id],
  );

  const ai = useAiNarrative({
    narrative,
    isEdit,
    notPublished,
    isFetching: isFetchingCurrentNarrative,
    content,
    setContent,
    setAutosave,
    tinyEditor,
  });

  // While a draft is previewed the footer counts the draft, the only place the user reads the count.
  const footerWordCount = ai.draftWordCount ?? getCurrentWordCount();

  useEffect(() => {
    if (iAmEditor) setIsEdit(true);
  }, [iAmEditor]);

  useEffect(() => {
    if (saved) {
      setIsEdit(false);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [saved]);

  useEffect(() => {
    if (canceled) {
      setIsEdit(false);
      setContent(initialContent);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [canceled]);

  useEffect(() => {
    if (autosaved) {
      showNotification(`Auto save applied for ${title}`, 'success', {
        autoClose: 3500,
      });
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [autosaved]);

  useEffect(() => {
    if (locked) {
      setIsEdit(true);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [locked]);

  useEffect(() => {
    if (selectedVersion !== null) setShowVersionsModal(false);
  }, [selectedVersion]);

  useEffect(() => {
    if (!isEdit) {
      setContent(initialContent);
    }
  }, [initialContent, isEdit]);

  function handleCancel() {
    dispatch(
      cancelMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
    setShowCancelModal(false);
  }

  function handleSave() {
    setShowSaveModal(false);

    const wordCount = getCurrentWordCount();

    dispatch(
      saveMultiNarrative({
        narrativeId: id,
        content,
        wordCount,
        autoSave: false,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function handleRevertVersion() {
    dispatch(
      restoreVersionMultiNarrative({
        narrativeVersionId: selectedVersion.id,
        content: selectedVersion.cleanContent,
        narrativeId: id,
      }),
    );
    setSelectedVersion(null);
  }

  function handleEditBtn() {
    sendGAEvent('edit_narrative_btn_clicked');
    dispatch(
      lockMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function handleForceEditBtn() {
    sendGAEvent('force_post_narrative_btn_clicked');
    dispatch(
      forceUnlockMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function getWordsCountResume() {
    return `${footerWordCount || 0} / ${limit}`;
  }

  function createComment(
    anchorName: string,
    anchorID: number,
    anchorContent: string,
  ) {
    const params = {
      url_format: {
        _office_: sectionDetailData.report.office_code,
        _report_: sectionDetailData.report.id,
        _section_: viewState.activeSectionID,
      },
      method: 'post',
      data: {
        content: anchorContent,
        title: anchorName,
        anchor: anchorName,
        anchor_id: anchorID,
      },
      storage_key: `${COMMENTS_LIST}_created`,
      success_actions: [
        sendSignalsAction({
          [undefined as any]: {
            [UPDATE]: true,
            [SUCCESS_REQUEST]: true,
          },
        }),
        requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
          url_format: { _id_: sectionDetailData.report.id },
        }),
      ],
    };

    dispatch(requestAPI(COMMENTS_LIST, params));
  }

  function onCommentAttachedToNarrative() {
    const requestKwargs = {
      url_format: {
        _office_: sectionDetailData.report.office_code,
        _report_: sectionDetailData.report.id,
        _section_: viewState.activeSectionID,
      },
      storage_key: COMMENTS_LIST,
    };
    dispatch(requestAPI(COMMENTS_LIST, requestKwargs));

    dispatch(
      sendSignalsAction({
        [COMMENTS_TARGET_ID]: { [UPDATE]: true },
      }),
    );
  }

  async function performAnchorAddingRequest(
    value: string,
    anchorName: string,
    anchorID: number,
    anchorComment: string,
    narrativeId?: number,
  ) {
    const url = `${API_HOST}/api/acr-sections/${viewState.activeSectionID}/add_anchors/`;
    const request_body = {
      content: value,
      editor: reportDetailData.username,
      narrative_id: narrativeId,
      title: anchorName,
    };

    try {
      const result = await apiCallAsync(url, request_body, 'post');

      dispatch(receiveMultiNarrativeSingleResponse(result.data));

      createComment(anchorName, anchorID, anchorComment);

      const requestKwargs = {
        url_format: {
          _office_: sectionDetailData.report.office_code,
          _report_: sectionDetailData.report.id,
          _section_: viewState.activeSectionID,
        },
        storage_key: COMMENTS_LIST,
      };

      dispatch(requestAPI(COMMENTS_LIST, requestKwargs));

      onCommentAttachedToNarrative();
      dispatch(changeViewStateAction({ draftComment: undefined }));
    } catch (e) {
      const msg = e.response?.data?.error;
      if (!msg) return;

      showNotification(msg, 'error');
    }
  }

  function onNarrativeChange(value: string) {
    setContent(value);
    if (!autosave && !ai.busyRef.current) setAutosave(true);
  }

  useEffect(() => {
    if (!autosave) {
      return;
    }

    const timer = setTimeout(() => {
      if (!isEdit || ai.busyRef.current || wordsExceed) {
        return;
      }

      const wordCount = getCurrentWordCount();

      dispatch(
        saveMultiNarrative({
          narrativeId: id,
          content,
          wordCount,
          autoSave: true,
        }),
      );
    }, narrativeAutosaveInterval);

    return () => clearTimeout(timer);
    // ai.busy: a timer skipped during an AI request is armed again once it ends.
  }, [autosave, content, isEdit, ai.busy, wordsExceed]);

  return (
    <>
      <Module
        className="multi-narrative-section"
        data-test-id={title.toLowerCase().split(' ').join('-')}
      >
        <ModuleHeader>
          <div className="multi-narrative-section-title">
            {title}
            {info && (
              <InfoCircle
                className="word-limit-panel-icon-info"
                width="24"
                height="24"
                color="#0b77c2"
                description=""
                data-test-id="information-icon"
                onClick={() => setShowInfoModal(true)}
              />
            )}
            {isOptional && <Tag type="info">Optional</Tag>}
          </div>

          <div className="multi-narrative-section-btns">
            <Button
              onClick={() => {
                setAutosave(false);
                sendGAEvent('narrative_versions_btn_clicked');
                setShowVersionsModal(true);
              }}
              kind="ghost"
              disabled={ai.narrativeBusy}
              data-test-id="versions-button"
            >
              Versions
            </Button>
            {isEdit && (
              <>
                {isEditEnable &&
                  notPublished &&
                  ai.primaryActions.map(({ action, label }) => (
                    <AiActionButton
                      key={action}
                      label={label}
                      isLoading={
                        ai.draftLoading && ai.preview?.action === action
                      }
                      disabled={ai.narrativeBusy || ai.quotaExhausted}
                      testId={`ai-action-${action}`}
                      onClick={() => ai.generateDraft(action)}
                    />
                  ))}
                <Button
                  onClick={() => {
                    setAutosave(false);
                    setShowCancelModal(true);
                  }}
                  kind="secondary"
                  disabled={ai.narrativeBusy}
                  data-test-id="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setAutosave(false);
                    setShowSaveModal(true);
                  }}
                  disabled={wordsExceed || ai.narrativeBusy}
                  data-test-id="save-version-button"
                >
                  Save version
                </Button>
              </>
            )}
            {!isEdit &&
              !narrativeEditedByOthers &&
              isEditEnable &&
              notPublished && (
                <Button
                  data-test-id="edit-button"
                  onClick={handleEditBtn}
                  disabled={isFetchingCurrentNarrative}
                >
                  Edit
                </Button>
              )}
            {!isEdit &&
              narrativeEditedByOthers &&
              notPublished &&
              isForcePostEnabled && (
                <Button
                  data-test-id="force-edit-button"
                  onClick={handleForceEditBtn}
                  disabled={isFetchingCurrentNarrative}
                >
                  Force Edit
                </Button>
              )}
          </div>
        </ModuleHeader>
        <ModuleBody>
          {isEdit && ai.canUseAi && (ai.quotaExhausted || ai.quotaLow) && (
            <AiQuotaNotice
              limit={ai.quota?.limit ?? 0}
              remaining={ai.quotaRemaining}
              exhausted={ai.quotaExhausted}
            />
          )}
          {isEdit &&
            ai.hint.visible &&
            !ai.quotaExhausted &&
            !ai.draftLoading &&
            !ai.draftReady && (
              <ContextualActionsHint
                onClose={ai.hint.close}
                onStopShowing={ai.hint.stopShowing}
              />
            )}
          {isEdit && ai.draftLoading && <DraftGenerationSkeleton />}
          <div ref={ai.editorAreaRef}>
            {isEdit &&
            !ai.draftLoading &&
            ai.draftReady &&
            ai.preview?.draftHtml ? (
              <DraftPreviewPanel
                draftHtml={ai.preview.draftHtml}
                currentContent={content}
                references={ai.preview.references || []}
                hasExistingContent={!!getPlainText(content).trim()}
                onAccept={ai.acceptDraft}
                onReject={ai.rejectDraft}
              />
            ) : (
              isEdit &&
              !ai.draftLoading && (
                <TinyEditorWrapper
                  content={content}
                  onChange={onNarrativeChange}
                  onEditorInit={setTinyEditor}
                  extraContentStyle={AI_SUGGESTION_CONTENT_STYLE}
                  suggestions={{ minWords: AI_MIN_SELECTION_WORDS }}
                />
              )
            )}
          </div>
          {notPublished &&
            !isEdit &&
            !narrativeEditedByOthers &&
            reportDetailData?.permissions?.comment_permissions?.add_issue &&
            (!content ? (
              <div className="narrative-empty-info">No narrative</div>
            ) : (
              <AnchorModal
                sectionId={viewState.activeSectionID}
                text={content}
                performAnchorAddingRequest={performAnchorAddingRequest}
                isExtra={false}
                narrativeId={id}
              />
            ))}
          {!isEdit &&
            (!notPublished ||
              narrativeEditedByOthers ||
              !reportDetailData?.permissions?.comment_permissions?.add_issue) &&
            (!content ? (
              <div className="narrative-empty-info">No narrative</div>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content),
                }}
              />
            ))}
        </ModuleBody>
        <ModuleFooter
          className={`${
            imEditing || narrativeEditedByOthers ? 'edit-mode' : ''
          }`}
        >
          {imEditing && lockedAt && (
            <div className="narrative-locked-msg">
              <Locked color="#04193B" />
              Narrative locked by you on
              {` ${format(lockedAt, 'dd MMMM yyyy, HH:mm')} (CEST)`}. It is not
              editable by others.
            </div>
          )}
          {narrativeEditedByOthers && lockedAt && (
            <div className="narrative-locked-msg">
              <Locked color="#04193B" />
              Narrative locked by {editor} on
              {` ${format(lockedAt, 'dd MMMM yyyy, HH:mm')} (CEST)`}. It is not
              editable by others.
            </div>
          )}
          {footerWordCount > limit ? (
            <NarrativeErrorMessage wordsCountResume={getWordsCountResume()} />
          ) : (
            <div data-test-id="word-counter" className="narrative-footer">
              Word count: {getWordsCountResume()}
            </div>
          )}
        </ModuleFooter>
      </Module>
      {showVersionsModal && (
        <VersionsModal
          title={title}
          onClose={() => setShowVersionsModal(false)}
          narrativeId={id}
          setSelectedVersion={setSelectedVersion}
          revertEnabled={
            !narrativeEditedByOthers && isEditEnable && !isEdit && notPublished
          }
        />
      )}
      {selectedVersion && (
        <ConfirmationModal
          open={true}
          onSubmit={handleRevertVersion}
          onClose={() => setSelectedVersion(null)}
          heading={`Revert to this version for ${title}?`}
          content={
            <>
              <div className="revert-version-subtitle">
                Are you sure you want to revert the narrative to this version?
              </div>
              <VersionItem
                content={selectedVersion.content}
                cleanContent={selectedVersion.cleanContent}
                editor={selectedVersion.editor}
                date={selectedVersion.date}
                id={selectedVersion.id}
              />
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
          wide
          customClassName="modal-revert-version"
        />
      )}
      {showSaveModal && (
        <ConfirmationModal
          open={true}
          onSubmit={handleSave}
          onClose={() => setShowSaveModal(false)}
          heading={`Save as a new version for ${title}?`}
          content={
            <>
              Are you sure you want to save your changes as a new version of
              this narrative?
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
        />
      )}
      {showCancelModal && (
        <ConfirmationModal
          open={true}
          onSubmit={handleCancel}
          onClose={() => setShowCancelModal(false)}
          heading={`Discard changes for ${title}?`}
          content={
            <>
              Are you sure you want to cancel your changes and go back to the
              previous version of this narrative?
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
        />
      )}
      <Modal
        open={showInfoModal}
        onRequestClose={() => setShowInfoModal(false)}
        modalHeading={title}
        passiveModal
        iconDescription="Close the modal"
        className="info-text-modal"
      >
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(info) }} />
      </Modal>
      <AiStatusAnnouncer
        draftLoading={ai.draftLoading}
        draftReady={ai.draftReady}
        rephraseLoading={ai.rephraseLoading}
        rephraseReady={ai.rephraseReady}
      />
    </>
  );
};

export default Narrative;
